import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import {
  Card,
  Alert,
  Table,
  Input,
  Button,
  Space,
  Tag,
  Modal,
  Form,
  message,
  Switch,
  Descriptions,
  Typography,
  Divider,
  Row,
  Col,
  Select,
  InputNumber,
  DatePicker,
  Image,
  Checkbox,
  Tooltip,
  Progress,
  Empty,
  Spin,
  Statistic,
  Timeline,
  Avatar,
  List
} from 'antd';
import dayjs from 'dayjs';
import {
  SearchOutlined,
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  EyeOutlined,
  ShoppingCartOutlined,
  GiftOutlined,
  DollarCircleOutlined,
  BarChartOutlined,
  ReloadOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  ClockCircleOutlined,
  RiseOutlined,
  FallOutlined,
  AppstoreOutlined,
  UnorderedListOutlined,
  ThunderboltOutlined,
  StarFilled,
  FireFilled,
  CopyOutlined,
  ExclamationCircleOutlined,
  DashboardOutlined,
  HistoryOutlined,
  ProfileOutlined,
  PieChartOutlined,
  LineChartOutlined
} from '@ant-design/icons';
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip as RTooltip, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';
import { compactParams, configAPI, mallAPI } from '../services/api';
import {OrderLookupInput, lookupColumns} from '../components/OrderLookupPanel';

const { TextArea } = Input;
const { Text, Title } = Typography;
const { Option } = Select;
const { RangePicker } = DatePicker;

const renderTableStack = (primary, secondary, meta) => (
  <div className="table-cell">
    <div className="table-cell-primary">{primary}</div>
    {secondary ? <div className="table-cell-secondary">{secondary}</div> : null}
    {meta ? <div className="table-cell-meta">{meta}</div> : null}
  </div>
);

const renderEmptyPanel = (title, subtitle) => (
  <div className="empty-state-panel">
    <div className="empty-state-title">{title}</div>
    {subtitle ? <div className="empty-state-subtitle">{subtitle}</div> : null}
  </div>
);

const ORDER_STATUS_THEME = {
  0: {
    label: '待支付',
    badge: { color: 'default' },
    icon: { node: <ClockCircleOutlined /> },
    surface: { avatarBg: '#faad14', border: '#ffe58f' }
  },
  1: {
    label: '已支付',
    badge: { color: 'blue' },
    icon: { node: <CheckCircleOutlined /> },
    surface: { avatarBg: '#1890ff', border: '#91caff' }
  },
  2: {
    label: '已发货',
    badge: { color: 'cyan' },
    icon: { node: <ThunderboltOutlined /> },
    surface: { avatarBg: '#13c2c2', border: '#87e8de' }
  },
  3: {
    label: '已完成',
    badge: { color: 'green' },
    icon: { node: <CheckCircleOutlined /> },
    surface: { avatarBg: '#52c41a', border: '#b7eb8f' }
  },
  4: {
    label: '已取消',
    badge: { color: 'red' },
    icon: { node: <CloseCircleOutlined /> },
    surface: { avatarBg: '#ff4d4f', border: '#ffccc7' }
  }
};

const PRODUCT_TYPE_THEME = {
  package: {
    label: '礼包',
    badge: { color: 'volcano' },
    icon: { glyph: '🎁' },
    surface: { gradient: 'linear-gradient(135deg, #ff6b6b, #ee5a24)', border: '#ffd8bf' }
  },
  diamond: {
    label: '钻石',
    badge: { color: 'cyan' },
    icon: { glyph: '💎' },
    surface: { gradient: 'linear-gradient(135deg, #4ecdc4, #2193b0)', border: '#b5f5ec' }
  },
  resource: {
    label: '资源',
    badge: { color: 'geekblue' },
    icon: { glyph: '📦' },
    surface: { gradient: 'linear-gradient(135deg, #667eea, #764ba2)', border: '#d6e4ff' }
  },
  aoz: {
    label: 'AOZ',
    badge: { color: 'purple' },
    icon: { glyph: '⚡' },
    surface: { gradient: 'linear-gradient(135deg, #a855f7, #6366f1)', border: '#efdbff' }
  },
  bubble_vip: {
    label: '泡点VIP',
    badge: { color: 'gold' },
    icon: { glyph: '👑' },
    surface: { gradient: 'linear-gradient(135deg, #f6d365, #fda085)', border: '#ffe58f' }
  }
};

const CURRENCY_THEME = {
  CNY: {
    label: '普通商城',
    symbol: '¥',
    badge: { color: 'red' },
    icon: { color: '#f5222d' },
    text: { accent: '#f5222d' },
    surface: { bg: '#fff1f0', border: '#ffccc7' }
  },
  TOKENS: {
    label: '代币',
    symbol: '币',
    badge: { color: 'purple' },
    icon: { color: '#722ed1' },
    text: { accent: '#722ed1' },
    surface: { bg: '#f9f0ff', border: '#efdbff' }
  },
  INTEGRAL: {
    label: '充值积分',
    symbol: '分',
    badge: { color: 'orange' },
    icon: { color: '#fa8c16' },
    text: { accent: '#fa8c16' },
    surface: { bg: '#fff7e6', border: '#ffd591' }
  },
  PROMOTION: {
    label: '推广积分',
    symbol: '分',
    badge: { color: 'cyan' },
    icon: { color: '#13c2c2' },
    text: { accent: '#13c2c2' },
    surface: { bg: '#e6fffb', border: '#87e8de' }
  }
};

const FALLBACK_ORDER_STATUS_THEME = {
  label: '未知',
  badge: { color: 'default' },
  icon: { node: null },
  surface: { avatarBg: '#d9d9d9', border: '#d9d9d9' }
};

const FALLBACK_PRODUCT_TYPE_THEME = {
  label: '未知类型',
  badge: { color: 'default' },
  icon: { glyph: '📦' },
  surface: { gradient: 'linear-gradient(135deg, #95a5a6, #7f8c8d)', border: '#d9d9d9' }
};

const FALLBACK_CURRENCY_THEME = {
  label: '普通商城',
  symbol: '¥',
  badge: { color: 'red' },
  icon: { color: '#f5222d' },
  text: { accent: '#f5222d' },
  surface: { bg: '#fff1f0', border: '#ffccc7' }
};

const getOrderStatusTheme = (status) => ORDER_STATUS_THEME[status] || FALLBACK_ORDER_STATUS_THEME;

const getProductTypeTheme = (type) => {
  const baseTheme = PRODUCT_TYPE_THEME[type];
  if (baseTheme) {
    return baseTheme;
  }

  return {
    ...FALLBACK_PRODUCT_TYPE_THEME,
    label: type || FALLBACK_PRODUCT_TYPE_THEME.label
  };
};

const getCurrencyTheme = (currencyType) => CURRENCY_THEME[currencyType] || FALLBACK_CURRENCY_THEME;

const renderFulfillmentState = (order) => {
  if (order?.product_type === 'admin_direct_grant') return <Tag color={Number(order.status) === 3 ? 'green' : 'orange'}>{Number(order.status) === 3 ? '后台直接发放完成' : '直接发放处理中/待核实（禁止重发）'}</Tag>;
  const total = Number(order?.fulfillment_total || 0);
  const delivered = Number(order?.fulfillment_delivered || 0);
  const failed = Number(order?.fulfillment_failed || 0);
  if (total === 0) return <Tag>无发货任务</Tag>;
  if (failed > 0) return <Tag color="red">发货失败 {failed}/{total}</Tag>;
  if (delivered >= total) return <Tag color="green">已全部到账 {delivered}/{total}</Tag>;
  return <Tag color="orange">发货中 {delivered}/{total}</Tag>;
};

const renderFirstChargeState = (status) => {
  if (status === null || status === undefined) return <Tag>无首充奖励</Tag>;
  if (Number(status) === 1) return <Tag color="green">首充奖励已到账</Tag>;
  if (Number(status) === 2) return <Tag color="red">首充奖励失败</Tag>;
  return <Tag color="orange">首充奖励待发放</Tag>;
};

const DIRECT_RESOURCE_PRESETS = [
  { id: 'diamond', name: '钻石', type: 'diamond', defaultLevel: 0 }
];

const CHART_COLORS = ['#667eea', '#764ba2', '#f093fb', '#f5576c', '#43e97b', '#38f9d7', '#4facfe', '#00f2fe'];

const buildMallItemPayload = (items = []) => (
  items.map((item) => {
    if (item.type === 'aoz') {
      let subtype = 'normal';
      if (item.id === 67174446) subtype = 'free';
      else if (item.id === 67174447) subtype = 'gm';

      return { type: 'aoz', id: item.id, count: item.count, name: item.name, subtype };
    }

    if (item.type === 'diamond') {
      return { type: 'diamond', id: item.id, count: item.count, name: item.name };
    }

    return { type: item.type || 'resource', id: item.id, count: item.count, name: item.name, level: item.level || 1 };
  })
);

const addUniqueSelectedItem = (items, item, onDuplicate) => {
  if (items.some((selectedItem) => selectedItem.uniqueId === item.uniqueId)) {
    onDuplicate?.();
    return items;
  }

  return [...items, item];
};

function MallManagement() {
  const [activeTab, setActiveTab] = useState('dashboard');
  const [loading, setLoading] = useState(false);
  const [viewMode, setViewMode] = useState('table');

  const [products, setProducts] = useState([]);
  const [productPagination, setProductPagination] = useState({ current: 1, pageSize: 20, total: 0 });
  const [productFilters, setProductFilters] = useState({ keyword: '', type: undefined, status: undefined });

  const [orders, setOrders] = useState([]);
  const [mallZoneOptions, setMallZoneOptions] = useState([]);
  const [paymentMethodOptions, setPaymentMethodOptions] = useState([]);
  const orderRequestRef = useRef(0);
  const paymentMethodLabel = (method) => paymentMethodOptions.find(option => option.value === method)?.label || method || '未支付';
  const [orderPagination, setOrderPagination] = useState({ current: 1, pageSize: 20, total: 0 });
  const [orderFilters, setOrderFilters] = useState({ status: undefined, start_date: undefined, end_date: undefined, order_no: undefined, account_name: undefined, server_id: undefined });
  const [orderSummaryDate, setOrderSummaryDate] = useState(new Date().toISOString().slice(0, 10));
  const [orderSummary, setOrderSummary] = useState({ zones: {} });
  const [manualOrderVisible, setManualOrderVisible] = useState(false);
  const [manualOrderForm] = Form.useForm();
  const [orderSearchText, setOrderSearchText] = useState('');
  const [orderQueryError, setOrderQueryError] = useState('');

  const [productModalVisible, setProductModalVisible] = useState(false);
  const [productForm] = Form.useForm();
  const productType = Form.useWatch('type', productForm);
  const [currentProduct, setCurrentProduct] = useState(null);
  const [modalMode, setModalMode] = useState('create');

  const [orderDetailVisible, setOrderDetailVisible] = useState(false);
  const [currentOrder, setCurrentOrder] = useState(null);

  const [itemList, setItemList] = useState([]);
  const [selectedItems, setSelectedItems] = useState([]);
  const [itemModalVisible, setItemModalVisible] = useState(false);
  const [itemSearchText, setItemSearchText] = useState('');
  const [levelModalVisible, setLevelModalVisible] = useState(false);
  const [currentItemForLevel, setCurrentItemForLevel] = useState(null);
  const [selectedLevel, setSelectedLevel] = useState(null);

  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [stats, setStats] = useState({ totalProducts: 0, totalOrders: 0, totalRevenue: 0, pendingOrders: 0 });
  const [statsScope, setStatsScope] = useState('all');
  const [orderTrend, setOrderTrend] = useState([]);
  const [productTypeDistribution, setProductTypeDistribution] = useState([]);
  const { current: productPageCurrent, pageSize: productPageSize } = productPagination;
  const { current: orderPageCurrent, pageSize: orderPageSize } = orderPagination;

  const currentProductTypeTheme = useMemo(
    () => (currentProduct ? getProductTypeTheme(currentProduct.type) : null),
    [currentProduct]
  );

  const currentProductCurrencyTheme = useMemo(
    () => (currentProduct ? getCurrencyTheme(currentProduct.currency_type) : null),
    [currentProduct]
  );

  const currentOrderStatusTheme = useMemo(
    () => (currentOrder ? getOrderStatusTheme(currentOrder.status) : null),
    [currentOrder]
  );

  const productTypeOptions = useMemo(
    () => Object.entries(PRODUCT_TYPE_THEME).map(([key, value]) => ({
      value: key,
      label: `${value.icon.glyph} ${value.label}`
    })),
    []
  );

  const currencyOptions = useMemo(
    () => Object.entries(CURRENCY_THEME).map(([key, value]) => ({
      value: key,
      label: `${value.symbol} ${value.label}`
    })),
    []
  );

  const orderStatusOptions = useMemo(
    () => Object.entries(ORDER_STATUS_THEME).map(([key, value]) => ({
      value: Number(key),
      label: value.label
    })),
    []
  );

  const productWorkspaceStats = useMemo(() => {
    const activeCount = products.filter((item) => item.status === 1).length;
    const hotCount = products.filter((item) => item.is_hot === 1).length;
    const lowStockCount = products.filter((item) => item.stock !== -1 && Number(item.stock || 0) <= 20).length;

    return [
      { label: '当前页商品', value: `${products.length}`, accent: '#2563eb' },
      { label: '上架商品', value: `${activeCount}`, accent: '#16a34a' },
      { label: '低库存', value: `${lowStockCount}`, accent: '#ea580c' },
      { label: '已选批量', value: `${selectedRowKeys.length}`, accent: '#7c3aed' },
      { label: '热销标记', value: `${hotCount}`, accent: '#dc2626' }
    ];
  }, [products, selectedRowKeys.length]);

  const orderWorkspaceStats = useMemo(() => {
    const paidCount = orders.filter((item) => item.status === 1 || item.status === 2 || item.status === 3).length;
    const completedCount = orders.filter((item) => item.status === 3).length;
    const canceledCount = orders.filter((item) => item.status === 4).length;
    const today = new Date().toISOString().slice(0, 10);
    const todayCount = orders.filter((item) => String(item.create_time || '').startsWith(today)).length;

    return [
      { label: '当前页订单', value: `${orders.length}`, accent: '#2563eb' },
      { label: '待支付', value: `${orders.filter((item) => item.status === 0).length}`, accent: '#ea580c' },
      { label: '已支付中', value: `${paidCount}`, accent: '#0f766e' },
      { label: '已完成', value: `${completedCount}`, accent: '#16a34a' },
      { label: '已取消', value: `${canceledCount}`, accent: '#dc2626' },
      { label: '今日订单', value: `${todayCount}`, accent: '#7c3aed' }
    ];
  }, [orders]);

  const mallWorkspaceTabs = useMemo(() => ([
    { key: 'dashboard', label: '运营总览', icon: <DashboardOutlined />, hint: '' },
    { key: 'products', label: '商品工作台', icon: <ShoppingCartOutlined />, hint: '' },
    { key: 'orders', label: '订单与充值查询', icon: <HistoryOutlined />, hint: '' }
  ]), []);

  const toSelectedItem = (item) => ({
    id: item.id,
    name: item.name,
    count: item.count || 1,
    level: item.level ?? item.defaultLevel ?? 0,
    uniqueId: item.uniqueId || `${item.id}_${item.level ?? item.defaultLevel ?? 0}`,
    type: item.type
  });

  const openProductModal = useCallback((mode, product = null, initialSelectedItems = []) => {
    setCurrentProduct(product);
    setModalMode(mode);
    setSelectedItems(initialSelectedItems);

    if (mode === 'create') {
      productForm.resetFields();
    }

    if (mode !== 'view') {
      const itemData = product ? parseProductConfig(product) : {};
      productForm.setFieldsValue(product ? {
        ...product,
        item_data: typeof product.item_data === 'string' ? product.item_data : JSON.stringify(product.item_data),
        vip_duration_value: itemData.duration_value,
        vip_duration_unit: itemData.duration_unit || 'day',
        first_charge_bonus_aoz: Object.prototype.hasOwnProperty.call(itemData, 'first_charge_bonus_aoz')
          ? Number(itemData.first_charge_bonus_aoz || 0)
          : undefined
      } : {});
    }

    setProductModalVisible(true);
  }, [productForm]);

  const loadStats = useCallback(async () => {
    try {
      const response = await mallAPI.getStats(
        statsScope === 'all' ? { all: 1 } : { server_id: Number(statsScope) }
      );
      const statsData = response.data?.data || {};

      setOrderTrend(Array.isArray(statsData.trend) ? statsData.trend : []);
      setStats({
        totalProducts: Number(statsData.totalProducts || 0),
        totalOrders: Number(statsData.totalOrders || 0),
        totalRevenue: Number(statsData.totalRevenue || 0).toFixed(2),
        pendingOrders: Number(statsData.pendingOrders || 0)
      });
    } catch (error) {
      console.error('Load stats error:', error);
    }
  }, [statsScope]);

  const loadOrderSummary = useCallback(async () => {
    try {
      const response = await mallAPI.getOrderSummary({
        date: orderSummaryDate,
        payment_method: orderFilters.payment_method
      });
      setOrderSummary(response.data?.data || { zones: {} });
    } catch (error) {
      message.error('加载分区流水失败');
    }
  }, [orderSummaryDate, orderFilters.payment_method]);

  useEffect(() => {
    let active = true;
    mallAPI.getOrderOptions().then(response => {
      if (!active) return;
      setMallZoneOptions(response.data?.data?.zones || []);
      setPaymentMethodOptions(response.data?.data?.paymentMethods || []);
    }).catch(() => { if (active) message.error('加载区服与支付方式失败，请刷新页面'); });
    return () => { active = false; };
  }, []);

  const handleCreateManualOrder = async (values) => {
    try {
      await mallAPI.createManualOrder(values);
      message.success('人工补订单已记录');
      setManualOrderVisible(false);
      manualOrderForm.resetFields();
      loadOrders();
    } catch (error) {
      message.error(error.response?.data?.message || '人工补订单失败');
    }
  };

  const loadProducts = useCallback(async () => {
    setLoading(true);
    try {
      const params = compactParams({ page: productPageCurrent, limit: productPageSize, ...productFilters });
      const response = await mallAPI.getProducts(params);
      const prods = response.data.data.products;
      setProducts(prods);
      const typeCounts = {};
      prods.forEach(p => { const t = p.type || 'other'; typeCounts[t] = (typeCounts[t] || 0) + 1; });
      setProductTypeDistribution(Object.entries(typeCounts).map(([name, value]) => ({ name: getProductTypeTheme(name).label, value })));
      setProductPagination(prev => ({ ...prev, total: response.data.data.pagination.total }));
    } catch (error) {
      message.error('加载商品列表失败');
    } finally {
      setLoading(false);
    }
  }, [productFilters, productPageCurrent, productPageSize]);

  const loadOrders = useCallback(async () => {
    const requestId = ++orderRequestRef.current;
    setLoading(true);
    setOrderQueryError('');
    try {
      const params = compactParams({ page: orderPageCurrent, limit: orderPageSize, ...orderFilters });
      const response = await (params.keyword ? mallAPI.lookupOrders(params) : mallAPI.getOrders(params));
      if (requestId !== orderRequestRef.current) return;
      setOrders(response.data.data.orders);
      setOrderPagination(prev => ({ ...prev, total: response.data.data.pagination.total }));
    } catch (error) {
      if (requestId === orderRequestRef.current) { setOrderQueryError(error.response?.data?.message || '订单查询失败，不代表没有订单'); setOrders([]); message.error('加载订单列表失败'); }
    } finally {
      if (requestId === orderRequestRef.current) setLoading(false);
    }
  }, [orderFilters, orderPageCurrent, orderPageSize]);

  const loadItemList = useCallback(async () => {
    try {
      const response = await configAPI.getItems();
      if (response.data.success) {
        const catalogItems = (response.data.data || []).map(item => ({
          id: item.item_id || item.id, name: item.name || `道具${item.item_id || item.id}`,
          type: item.type || 'resource', levels: item.levels || [],
          hasMultipleLevels: item.hasMultipleLevels || false, defaultLevel: item.defaultLevel || null
        }));
        setItemList([...DIRECT_RESOURCE_PRESETS, ...catalogItems]);
      } else { setItemList([]); }
    } catch (error) { setItemList([]); }
  }, []);

  useEffect(() => {
    if (activeTab === 'products' || activeTab === 'dashboard') { loadProducts(); loadItemList(); }
    if (activeTab === 'orders' || activeTab === 'dashboard') { loadOrders(); loadOrderSummary(); }
    loadStats();
  }, [activeTab, loadProducts, loadOrders, loadStats, loadItemList, loadOrderSummary]);

  const handleCreateProduct = () => {
    openProductModal('create');
  };

  const parseProductConfig = (product) => {
    try {
      return typeof product.item_data === 'string' ? JSON.parse(product.item_data) : (product.item_data || {});
    } catch (error) {
      return {};
    }
  };

  const handleEditProduct = (product) => {
    const itemData = parseProductConfig(product);
    try {
      openProductModal('edit', product, itemData?.items && Array.isArray(itemData.items) ? itemData.items.map(toSelectedItem) : []);
    } catch (error) {
      openProductModal('edit', product, []);
    }
  };

  const handleViewProduct = (product) => {
    try {
      const itemData = parseProductConfig(product);
      openProductModal('view', product, itemData?.items && Array.isArray(itemData.items) ? itemData.items.map(toSelectedItem) : []);
    } catch (error) {
      openProductModal('view', product, []);
    }
  };

  const handleProductSubmit = async (values) => {
    try {
      const defaultProductImageUrl = '/images/mall/gift-chest.png';
      const itemData = values.type === 'bubble_vip'
        ? {
            mode: 'bubble_vip',
            duration_value: Math.max(1, Number(values.vip_duration_value) || 30),
            duration_unit: values.vip_duration_unit || 'day',
            items: []
          }
        : {
            items: buildMallItemPayload(selectedItems)
          };
      if (values.type === 'aoz' && values.first_charge_bonus_aoz !== undefined && values.first_charge_bonus_aoz !== null && values.first_charge_bonus_aoz !== '') {
        itemData.first_charge_bonus_aoz = Math.max(0, Math.floor(Number(values.first_charge_bonus_aoz) || 0));
      }
      const submitValues = {
        ...values, image_url: values.image_url || defaultProductImageUrl,
        item_data: JSON.stringify(itemData)
      };
      delete submitValues.vip_duration_value;
      delete submitValues.vip_duration_unit;
      delete submitValues.first_charge_bonus_aoz;
      if (currentProduct) { await mallAPI.updateProduct(currentProduct.id, submitValues); message.success('商品更新成功'); }
      else { await mallAPI.createProduct(submitValues); message.success('商品创建成功'); }
      setProductModalVisible(false); productForm.resetFields(); setSelectedItems([]); loadProducts();
    } catch (error) { message.error(error.response?.data?.message || '保存商品失败'); }
  };

  const handleDeleteProduct = (product) => {
    Modal.confirm({
      title: '确认删除', icon: <ExclamationCircleOutlined />, content: `确定要删除商品"${product.name}"吗？此操作不可撤销。`,
      okText: '确认删除', okType: 'danger', cancelText: '取消',
      onOk: async () => { try { await mallAPI.deleteProduct(product.id); message.success('商品删除成功'); loadProducts(); } catch (error) { message.error('删除商品失败'); } }
    });
  };

  const handleBatchDelete = async () => {
    if (selectedRowKeys.length === 0) return message.warning('请选择要删除的商品');
    Modal.confirm({
      title: '批量删除确认', icon: <ExclamationCircleOutlined />, content: `确定要删除选中的 ${selectedRowKeys.length} 件商品吗？`,
      okText: '确认删除', okType: 'danger',
      onOk: async () => { try { await Promise.all(selectedRowKeys.map(id => mallAPI.deleteProduct(id))); message.success(`成功删除 ${selectedRowKeys.length} 件商品`); setSelectedRowKeys([]); loadProducts(); } catch (error) { message.error('批量删除失败'); } }
    });
  };

  const handleBatchStatus = async (status) => {
    if (selectedRowKeys.length === 0) return message.warning('请选择要操作的商品');
    try { await mallAPI.batchUpdateStatus({ ids: selectedRowKeys, status }); message.success(`成功${status === 1 ? '上架' : '下架'} ${selectedRowKeys.length} 件商品`); setSelectedRowKeys([]); loadProducts(); }
    catch (error) { message.error('批量操作失败'); }
  };

  const handleToggleStatus = async (product) => {
    try { await mallAPI.updateProduct(product.id, { ...product, status: product.status === 1 ? 0 : 1 }); message.success(`商品已${product.status === 1 ? '下架' : '上架'}`); loadProducts(); }
    catch (error) { message.error('操作失败'); }
  };

  const handleSelectItem = (item) => {
    if (item.type === 'diamond') {
      const uniqueId = `${item.id}_0`;
      setSelectedItems((prevItems) => addUniqueSelectedItem(
        prevItems,
        { ...item, level: 0, count: 1, uniqueId },
        () => message.info('该资源已添加到列表中')
      ));
      return;
    }
    if (item.levels && item.levels.length > 0) { setCurrentItemForLevel(item); setSelectedLevel(item.levels[0].level); setLevelModalVisible(true); return; }
    const level = item.defaultLevel || 1; const uniqueId = `${item.id}_${level}`;
    setSelectedItems((prevItems) => addUniqueSelectedItem(
      prevItems,
      { ...item, level, count: 1, uniqueId },
      () => message.info('该道具已添加到列表中')
    ));
  };

  const handleConfirmLevel = () => {
    if (!currentItemForLevel || !selectedLevel) return;
    const uniqueId = `${currentItemForLevel.id}_${selectedLevel}`;
    setSelectedItems((prevItems) => addUniqueSelectedItem(
      prevItems,
      { ...currentItemForLevel, level: selectedLevel, count: 1, uniqueId },
      () => message.info('该道具已添加到列表中')
    ));
    setLevelModalVisible(false); setCurrentItemForLevel(null); setSelectedLevel(null);
  };

  const handleRemoveItem = (uniqueId) => { setSelectedItems(selectedItems.filter(i => (i.uniqueId || i.id) !== uniqueId)); };

  const handleUpdateItemCount = (uniqueId, count) => {
    setSelectedItems(selectedItems.map(i => (i.uniqueId || i.id) === uniqueId ? { ...i, count: parseInt(count) || 1 } : i));
  };

  const handleConfirmItems = () => {
    const itemData = { items: buildMallItemPayload(selectedItems) };
    productForm.setFieldsValue({ item_data: JSON.stringify(itemData, null, 2) }); setItemModalVisible(false);
  };

  const handleViewOrderDetail = async (order) => {
    try { const response = await mallAPI.getOrder(order.id); setCurrentOrder(response.data.data); setOrderDetailVisible(true); }
    catch (error) { message.error('加载订单详情失败'); }
  };

  const handleUpdateOrderStatus = async (orderId, status) => {
    try { await mallAPI.updateOrderStatus(orderId, { status }); message.success('订单状态更新成功'); setOrderDetailVisible(false); loadOrders(); }
    catch (error) { message.error('更新订单状态失败'); }
  };

  const productColumns = [
    {
      title: <Checkbox indeterminate={selectedRowKeys.length > 0 && selectedRowKeys.length < products.length} checked={selectedRowKeys.length === products.length && products.length > 0} onChange={(e) => setSelectedRowKeys(e.target.checked ? products.map(p => p.id) : [])} />,
      dataIndex: 'id', width: 48,
      render: (id) => <Checkbox checked={selectedRowKeys.includes(id)} onChange={() => setSelectedRowKeys(selectedRowKeys.includes(id) ? selectedRowKeys.filter(k => k !== id) : [...selectedRowKeys, id])} />
    },
    {
      title: '商品', key: 'product', width: 300,
      render: (_, record) => {
        const typeTheme = getProductTypeTheme(record.type);
        const currencyTheme = getCurrencyTheme(record.currency_type);
        return (
          <div className="mall-product-cell">
            <div className="mall-product-thumb mall-product-thumb-dynamic" style={{ '--mall-thumb-bg': typeTheme.surface.gradient }}>
              {record.image_url ? <Image src={record.image_url} width={52} height={52} className="mall-product-thumb-image" preview={false} /> : <span className="mall-product-thumb-icon">{typeTheme.icon.glyph}</span>}
            </div>
            <div className="text-truncate-min">
              {renderTableStack(
                record.name,
                <div className="table-tag-row">
                  <Tag color={typeTheme.badge.color} className="mall-tag-sm">{typeTheme.label}</Tag>
                  {record.is_hot === 1 && <Tag color="red" className="mall-tag-sm"><FireFilled /> 热</Tag>}
                  {record.is_new === 1 && <Tag color="green" className="mall-tag-sm"><StarFilled /> 新</Tag>}
                  <Tag color={currencyTheme.badge.color} className="mall-tag-sm">{currencyTheme.label}</Tag>
                </div>,
                `ID ${record.id} / 排序 ${record.sort_order || 0}`
              )}
            </div>
          </div>
        );
      }
    },
    {
      title: '价格', key: 'price', width: 170, sorter: (a, b) => a.current_price - b.current_price,
      render: (_, record) => {
        const currencyTheme = getCurrencyTheme(record.currency_type);
        return (
          <div>
            {record.original_price > 0 && record.original_price !== record.current_price && <Text delete className="mall-price-original">{currencyTheme.symbol}{record.original_price}</Text>}
            <div><Text strong className="mall-price-current mall-dynamic-color" style={{ '--mall-dynamic-color': currencyTheme.text.accent }}>{currencyTheme.symbol}{record.currency_price || record.current_price}</Text>
              {record.discount && record.discount < 1 && record.discount > 0 && <Tag color="red" className="mall-discount-tag">{Math.round(record.discount * 10)}折</Tag>}
            </div>
            <Text type="secondary" className="mall-currency-text">{currencyTheme.label}</Text>
          </div>
        );
      }
    },
    {
      title: '库存', dataIndex: 'stock', width: 120, sorter: (a, b) => a.stock - b.stock,
      render: (stock) => stock === -1 ? <Tag color="green" className="tag-tight">无限</Tag> :
        <Progress percent={Math.min((stock / 100) * 100, 100)} size="small" format={() => stock} status={stock > 20 ? 'normal' : 'exception'} strokeColor={stock > 20 ? '#52c41a' : stock > 0 ? '#faad14' : '#ff4d4f'} />
    },
    { title: '排序', dataIndex: 'sort_order', width: 70, sorter: (a, b) => a.sort_order - b.sort_order, align: 'center' },
    { title: '状态', dataIndex: 'status', width: 110, render: (status, record) => <Switch checked={status === 1} checkedChildren="上架" unCheckedChildren="下架" size="small" onChange={() => handleToggleStatus(record)} /> },
    {
      title: '操作', key: 'action', width: 140, fixed: 'right',
      render: (_, record) => (
        <Space size={2}>
          <Tooltip title="查看"><Button type="text" size="small" icon={<EyeOutlined />} onClick={() => handleViewProduct(record)} /></Tooltip>
          <Tooltip title="编辑"><Button type="text" size="small" icon={<EditOutlined />} onClick={() => handleEditProduct(record)} /></Tooltip>
          <Tooltip title="复制"><Button type="text" size="small" icon={<CopyOutlined />} onClick={() => {
            handleCreateProduct();
            setTimeout(() => {
              productForm.setFieldsValue({ ...record, id: undefined, item_data: typeof record.item_data === 'string' ? record.item_data : JSON.stringify(record.item_data) });
              try { const itemData = typeof record.item_data === 'string' ? JSON.parse(record.item_data) : record.item_data; if (itemData?.items) setSelectedItems(itemData.items.map(toSelectedItem)); } catch (e) { /* ignore */ }
            }, 100);
          }} /></Tooltip>
          <Tooltip title="删除"><Button type="text" size="small" danger icon={<DeleteOutlined />} onClick={() => handleDeleteProduct(record)} /></Tooltip>
        </Space>
      )
    }
  ];

  const orderColumns = [
    { title: '订单号', dataIndex: 'order_no', width: 200, render: (text) => <Text code copyable={{ text, icon: <CopyOutlined /> }}>{text}</Text> },
    ...(orderFilters.keyword ? lookupColumns.filter(column => column.dataIndex === 'order_sources' || column.key === 'policy') : []),
    { title: '区服', dataIndex: 'server_id', width: 70, align: 'center', render: (serverId) => serverId ? `${serverId}区` : '-' },
    { title: '后台账号ID', dataIndex: 'user_id', width: 100, render: (id) => <Text copyable={{ text: String(id ?? '') }}>{id ?? '-'}</Text> },
    {
      title: '游戏账号',
      key: 'account',
      width: 210,
      render: (_, record) => renderTableStack(
        record.target_account_name || `用户ID ${record.user_id}`,
        `付款：${record.payer_account_name || record.target_account_name || record.payer_user_id || record.user_id || '-'}`,
        record.character_name ? `角色 ${record.character_name} / ID ${record.character_id || '-'}` : null
      )
    },
    { title: '商品', dataIndex: 'product_name', width: 180, ellipsis: true, render: (text, record) => renderTableStack(text || '未命名商品', `数量 ${record.quantity || 0}`, record.payment_method ? `支付方式 ${paymentMethodLabel(record.payment_method)}` : '尚未支付') },
    { title: '数量', dataIndex: 'quantity', width: 70, align: 'center' },
    { title: '金额', dataIndex: 'total_amount', width: 110, sorter: (a, b) => a.total_amount - b.total_amount, render: (amount) => <Text strong type="danger" className="text-size-md">¥{amount}</Text> },
    { title: '支付方式', dataIndex: 'payment_method', width: 160, render: (method) => method ? <Tag color="purple" className="tag-tight">{paymentMethodLabel(method)}</Tag> : <Tag>未支付</Tag> },
    { title: '状态', dataIndex: 'status', width: 110, render: (status) => { const statusTheme = getOrderStatusTheme(status); return <Tag color={statusTheme.badge.color} icon={statusTheme.icon.node} className="tag-tight">{statusTheme.label}</Tag>; } },
    { title: '下单IP', dataIndex: 'client_ip', width: 140, render: (ip) => ip ? <Text code copyable={{ text: String(ip) }}>{ip}</Text> : <Text type="secondary">-</Text> },
    { title: '创建时间', dataIndex: 'create_time', width: 170, sorter: (a, b) => new Date(a.create_time) - new Date(b.create_time), render: (time) => renderTableStack(new Date(time).toLocaleString('zh-CN'), '下单时间') },
    { title: '操作', key: 'action', width: 80, fixed: 'right', render: (_, record) => <Button type="link" size="small" icon={<EyeOutlined />} onClick={() => handleViewOrderDetail(record)}>详情</Button> }
  ];

  const renderDashboard = () => (
    <div className="mall-overview-layout">
      <div className="compact-stat-row block-gap-lg">
        {[
          { title: '商品总数', value: `${stats.totalProducts} 件`, icon: <ShoppingCartOutlined />, color: '#2563eb' },
          { title: '订单总数', value: `${stats.totalOrders} 笔`, icon: <BarChartOutlined />, color: '#16a34a' },
          { title: '总销售额', value: `¥${stats.totalRevenue}`, icon: <DollarCircleOutlined />, color: '#db2777' },
          { title: '待处理订单', value: `${stats.pendingOrders} 笔`, icon: <ClockCircleOutlined />, color: '#ea580c' }
        ].map((item) => (
          <div className="compact-stat" key={item.title}>
            <div className="compact-stat-label">{item.title}</div>
            <div className="compact-stat-value mall-dynamic-color" style={{ '--mall-dynamic-color': item.color }}>{item.value}</div>
            <div className="compact-stat-icon trend-row mall-dynamic-color" style={{ '--mall-dynamic-color': item.color }}>{item.icon}</div>
          </div>
        ))}
      </div>

      <div className="mall-overview-grid">
        <Card className="section-card mall-overview-card" title={<span><LineChartOutlined className="mall-chart-title-icon-blue" />近7日订单趋势</span>} bodyStyle={{ padding: '16px 24px 8px' }}>
          <ResponsiveContainer width="100%" height={300}>
            <AreaChart data={orderTrend}>
              <defs>
                <linearGradient id="colorOrders" x1="0" y1="0" x2="0" y2="1"><stop offset="5%" stopColor="#667eea" stopOpacity={0.3} /><stop offset="95%" stopColor="#667eea" stopOpacity={0} /></linearGradient>
                <linearGradient id="colorRevenue" x1="0" y1="0" x2="0" y2="1"><stop offset="5%" stopColor="#f5576c" stopOpacity={0.3} /><stop offset="95%" stopColor="#f5576c" stopOpacity={0} /></linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
              <XAxis dataKey="date" tick={{ fontSize: 12 }} />
              <YAxis yAxisId="left" tick={{ fontSize: 12 }} />
              <YAxis yAxisId="right" orientation="right" tick={{ fontSize: 12 }} />
              <RTooltip />
              <Area yAxisId="left" type="monotone" dataKey="orders" stroke="#667eea" fillOpacity={1} fill="url(#colorOrders)" strokeWidth={2} name="订单数" />
              <Area yAxisId="right" type="monotone" dataKey="revenue" stroke="#f5576c" fillOpacity={1} fill="url(#colorRevenue)" strokeWidth={2} name="销售额" />
            </AreaChart>
          </ResponsiveContainer>
        </Card>

        <div className="mall-overview-side">
          <Card className="section-card mall-overview-card" title={<span><PieChartOutlined className="mall-chart-title-icon-purple" />商品类型分布</span>} bodyStyle={{ padding: '16px 24px' }}>
            {productTypeDistribution.length > 0 ? (
              <ResponsiveContainer width="100%" height={280}>
                <PieChart><Pie data={productTypeDistribution} cx="50%" cy="50%" innerRadius={60} outerRadius={100} paddingAngle={4} dataKey="value" label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}>{productTypeDistribution.map((entry, index) => <Cell key={`cell-${index}`} fill={CHART_COLORS[index % CHART_COLORS.length]} />)}</Pie><RTooltip /></PieChart>
              </ResponsiveContainer>
            ) : <Empty description="暂无数据" className="mall-empty-offset" />}
          </Card>

          <div className="mall-overview-focus">
            <div className="mall-focus-card">
              <div className="mall-focus-card-label">待处理订单</div>
              <div className="mall-focus-card-value">待处理 {stats.pendingOrders} 笔</div>
            </div>
            <div className="mall-focus-card">
              <div className="mall-focus-card-label">商品类型</div>
              <div className="mall-focus-card-value">{productTypeDistribution.length} 类商品</div>
            </div>
          </div>
        </div>
      </div>

      <div className="mall-activity-grid">
        <Card className="section-card" title={<span><ShoppingCartOutlined className="mall-chart-title-icon-green" />最近商品</span>} bodyStyle={{ padding: 0 }}>
          <List dataSource={products.slice(0, 5)} renderItem={item => {
            const typeTheme = getProductTypeTheme(item.type);
            const currencyTheme = getCurrencyTheme(item.currency_type);
            return (
              <List.Item className="mall-list-item" onClick={() => handleViewProduct(item)}>
                <List.Item.Meta avatar={<Avatar className="mall-list-avatar mall-list-avatar-dynamic" style={{ '--mall-avatar-bg': typeTheme.surface.gradient }} size={44}>{typeTheme.icon.glyph}</Avatar>} title={<Text strong className="mall-list-title">{item.name}</Text>}
                  description={<Space size={4}><Tag color={typeTheme.badge.color} className="mall-tag-xs">{typeTheme.label}</Tag><Text type="danger" strong className="mall-list-title">{currencyTheme.symbol}{item.currency_price || item.current_price}</Text></Space>} />
                <Tag color={item.status === 1 ? 'green' : 'default'} className="tag-tight">{item.status === 1 ? '在售' : '下架'}</Tag>
              </List.Item>
            );
          }} />
        </Card>

        <Card className="section-card" title={<span><HistoryOutlined className="mall-chart-title-icon-red" />最近订单</span>} bodyStyle={{ padding: 0 }}>
          <List dataSource={orders.slice(0, 5)} renderItem={item => {
            const statusTheme = getOrderStatusTheme(item.status);
            return (
              <List.Item className="mall-list-item" onClick={() => handleViewOrderDetail(item)}>
                <List.Item.Meta avatar={<Avatar className="mall-list-avatar mall-list-avatar-dynamic" style={{ '--mall-avatar-bg': statusTheme.surface.avatarBg }} size={44} icon={statusTheme.icon.node} />} title={<Text strong className="mall-list-title">{item.product_name || item.order_no}</Text>}
                  description={<Space size={4}><Text type="secondary" className="mall-list-subtext">用户 {item.user_id}</Text><Text type="danger" strong className="mall-list-title">¥{item.total_amount}</Text></Space>} />
                <Tag color={statusTheme.badge.color} className="tag-tight">{statusTheme.label}</Tag>
              </List.Item>
            );
          }} />
        </Card>
      </div>
    </div>
  );

  const renderProductGrid = () => (
    <Spin spinning={loading}>
      <Row gutter={[16, 16]}>
        {products.map(product => {
          const typeTheme = getProductTypeTheme(product.type);
          const currencyTheme = getCurrencyTheme(product.currency_type);
          const cardBorder = product.status === 1 ? '1px solid #f0f0f0' : '1px dashed #d9d9d9';
          return (
            <Col key={product.id} xs={24} sm={12} md={8} lg={6}>
              <Card hoverable className="mall-grid-card mall-grid-card-dynamic" style={{ '--mall-card-border': cardBorder, '--mall-card-opacity': product.status === 1 ? 1 : 0.7 }}
                cover={
                  <div className="mall-grid-cover mall-grid-cover-dynamic" style={{ '--mall-cover-bg': typeTheme.surface.gradient }}>
                    {product.image_url ? <Image src={product.image_url} alt={product.name} className="mall-grid-image" preview={false} /> : <span className="mall-grid-icon">{typeTheme.icon.glyph}</span>}
                    <div className="mall-grid-badges">
                      {product.is_hot === 1 && <Tag color="red" className="tag-tight"><FireFilled /> 热</Tag>}
                      {product.is_new === 1 && <Tag color="green" className="tag-tight"><StarFilled /> 新</Tag>}
                    </div>
                    {product.status !== 1 && <div className="mall-grid-status">已下架</div>}
                  </div>
                }
                actions={[
                  <Tooltip title="编辑"><EditOutlined onClick={() => handleEditProduct(product)} /></Tooltip>,
                  <Tooltip title={product.status === 1 ? '下架' : '上架'}>{product.status === 1 ? <FallOutlined onClick={() => handleToggleStatus(product)} /> : <RiseOutlined onClick={() => handleToggleStatus(product)} />}</Tooltip>,
                  <Tooltip title="删除"><DeleteOutlined className="text-accent-danger" onClick={() => handleDeleteProduct(product)} /></Tooltip>
                ]}
              >
                <Card.Meta title={<Text strong ellipsis>{product.name}</Text>} description={
                  <div>
                    <div className="mall-grid-price-row"><Text strong className="mall-price-current mall-dynamic-color" style={{ '--mall-dynamic-color': currencyTheme.text.accent }}>{currencyTheme.symbol}{product.currency_price || product.current_price}</Text>
                      {product.discount && product.discount < 1 && product.discount > 0 && <Tag color="red" className="mall-discount-tag">{Math.round(product.discount * 10)}折</Tag>}
                    </div>
                    <Space size={4} wrap>
                      <Tag color={typeTheme.badge.color} className="mall-tag-sm">{typeTheme.label}</Tag>
                      <Tag color={currencyTheme.badge.color} className="mall-tag-sm">{currencyTheme.label}</Tag>
                      <Tag color={product.stock === -1 ? 'green' : product.stock > 0 ? 'default' : 'red'} className="mall-tag-sm">{product.stock === -1 ? '无限' : `剩${product.stock}`}</Tag>
                    </Space>
                  </div>
                } />
              </Card>
            </Col>
          );
        })}
      </Row>
    </Spin>
  );

  const renderBatchActions = () => {
    if (selectedRowKeys.length === 0) return null;
    return (
      <div className="mall-batch-bar">
        <div className="mall-batch-bar-main">
          <div className="mall-batch-bar-value">{selectedRowKeys.length}</div>
          <div>
            <div className="mall-batch-bar-title">批量处理已激活</div>
          </div>
        </div>
        <div className="mall-batch-bar-actions">
          <Button size="small" onClick={() => handleBatchStatus(1)} icon={<RiseOutlined />}>批量上架</Button>
          <Button size="small" onClick={() => handleBatchStatus(0)} icon={<FallOutlined />}>批量下架</Button>
          <Button size="small" danger onClick={handleBatchDelete} icon={<DeleteOutlined />}>批量删除</Button>
          <Button size="small" onClick={() => setSelectedRowKeys([])}>清空选择</Button>
        </div>
      </div>
    );
  };

  const renderOrderTimeline = (order) => {
    const steps = [
      { title: '创建订单', time: order.create_time, status: 'done' },
      { title: '支付完成', time: order.payment_time, status: order.status >= 1 ? 'done' : order.status === 0 ? 'current' : 'wait' },
      { title: '已发货', time: order.ship_time, status: order.status >= 2 ? 'done' : order.status === 1 ? 'current' : 'wait' },
      { title: '已完成', time: order.finish_time, status: order.status >= 3 ? 'done' : order.status === 2 ? 'current' : 'wait' }
    ];
    if (order.status === 4) steps.push({ title: '已取消', time: order.cancel_time, status: 'error' });
    return <Timeline items={steps.map(step => ({ color: step.status === 'done' ? 'green' : step.status === 'current' ? 'blue' : step.status === 'error' ? 'red' : 'gray', children: <div><Text strong={step.status === 'current'}>{step.title}</Text>{step.time && <div><Text type="secondary" className="mall-timeline-time">{new Date(step.time).toLocaleString('zh-CN')}</Text></div>}</div> }))} />;
  };

  return (
    <div className="page-shell mall-workspace-shell">
      <div className="mall-command-deck">
        <div className="mall-command-deck-head">
          <div className="mall-command-copy">
            <div className="mall-command-title">商城管理</div>
            <Space wrap size={8} style={{ marginTop: 10 }}>
              <Text type="secondary">销售统计范围</Text>
              <Select
                value={statsScope}
                style={{ width: 160 }}
                onChange={setStatsScope}
                options={[
                  { value: 'all', label: '所有大区' },
                  ...mallZoneOptions.map((zone) => ({ value: String(zone.value), label: zone.label }))
                ]}
              />
              <Button size="small" icon={<ReloadOutlined />} onClick={loadStats}>刷新</Button>
            </Space>
            <div style={{ marginTop: 8 }}><Text type="secondary">销售口径：在线支付＋客服代充，不含管理员补发及平台币消费；返佣和累充规则不变。</Text></div>
          </div>
          <div className="mall-command-metrics">
            <div className="mall-command-metric">
              <span>商品总量</span>
              <strong>{stats.totalProducts}</strong>
            </div>
            <div className="mall-command-metric">
              <span>订单总量</span>
              <strong>{stats.totalOrders}</strong>
            </div>
            <div className="mall-command-metric">
              <span>销售额</span>
              <strong>¥{stats.totalRevenue}</strong>
            </div>
          </div>
        </div>

        <div className="mall-workspace-tabs">
          {mallWorkspaceTabs.map((tab) => (
            <button
              key={tab.key}
              type="button"
              className={`mall-workspace-tab${activeTab === tab.key ? ' mall-workspace-tab-active' : ''}`}
              onClick={() => setActiveTab(tab.key)}
            >
              <span className="mall-workspace-tab-icon">{tab.icon}</span>
              <span className="mall-workspace-tab-body">
                <strong>{tab.label}</strong>
                <span>{tab.hint}</span>
              </span>
            </button>
          ))}
        </div>
      </div>

      {activeTab === 'dashboard' && (
        <>
          <div className="mall-section-banner">
            <div className="mall-section-banner-copy">
              <div className="mall-section-banner-title">运营总览</div>
            </div>
            <div className="mall-section-banner-tags">
              <Tag color="blue">商品 {productPagination.total || 0}</Tag>
              <Tag color="green">订单 {orderPagination.total || 0}</Tag>
              <Tag color="orange">待处理 {stats.pendingOrders}</Tag>
            </div>
          </div>
          {renderDashboard()}
        </>
      )}

      {activeTab === 'products' && (
        <>
          <div className="mall-section-banner">
            <div className="mall-section-banner-copy">
              <div className="mall-section-banner-title">商品工作台</div>
            </div>
            <div className="mall-section-banner-tags">
              {productWorkspaceStats.map((item) => (
                <div key={item.label} className="mall-inline-stat">
                  <span>{item.label}</span>
                  <strong className="mall-dynamic-color" style={{ '--mall-dynamic-color': item.accent }}>{item.value}</strong>
                </div>
              ))}
            </div>
          </div>

          <div className="mall-command-panel">
            <div className="mall-command-panel-main">
              <Input.Search placeholder="搜索商品名称" className="field-width-240" onSearch={(value) => { setProductFilters(prev => ({ ...prev, keyword: value })); setProductPagination(prev => ({ ...prev, current: 1 })); }} allowClear prefix={<SearchOutlined />} />
              <Select placeholder="请选择商品类型" className="field-width-120" allowClear onChange={(value) => { setProductFilters(prev => ({ ...prev, type: value })); setProductPagination(prev => ({ ...prev, current: 1 })); }}>{productTypeOptions.map((option) => <Option key={option.value} value={option.value}>{option.label}</Option>)}</Select>
              <Select placeholder="状态" className="field-width-100" allowClear onChange={(value) => { setProductFilters(prev => ({ ...prev, status: value })); setProductPagination(prev => ({ ...prev, current: 1 })); }}><Option value={1}>上架</Option><Option value={0}>下架</Option></Select>
            </div>
            <div className="mall-command-panel-actions">
              <div className="mall-view-switch">
                <Button type={viewMode === 'table' ? 'primary' : 'default'} icon={<UnorderedListOutlined />} size="small" onClick={() => setViewMode('table')} className="rounded-button">表格</Button>
                <Button type={viewMode === 'grid' ? 'primary' : 'default'} icon={<AppstoreOutlined />} size="small" onClick={() => setViewMode('grid')} className="rounded-button">卡片</Button>
              </div>
              <Button type="primary" icon={<PlusOutlined />} onClick={handleCreateProduct} className="rounded-button">创建商品</Button>
              <Button icon={<ReloadOutlined />} onClick={loadProducts} className="rounded-button">刷新</Button>
            </div>
          </div>

          {renderBatchActions()}

          <Card className="section-card mall-data-card" bodyStyle={{ padding: activeTab === 'products' && viewMode === 'grid' ? '20px' : 0 }}>
            <div className="mall-data-card-head">
              <div>
                <div className="mall-data-card-title">商品列表</div>
              </div>
              <Tag color="blue">当前页 {products.length} 条</Tag>
            </div>
            {viewMode === 'table' ? (
              <Table columns={productColumns} dataSource={products} loading={loading} pagination={{ ...productPagination, showSizeChanger: true, showTotal: (total) => `共 ${total} 条`, onChange: (page, pageSize) => setProductPagination(prev => ({ ...prev, current: page, pageSize: pageSize || 20 })) }} rowKey="id" scroll={{ x: 1100 }} className="table-top-gap" />
            ) : renderProductGrid()}
          </Card>
        </>
      )}

      {activeTab === 'orders' && (
        <>
          <div className="mall-section-banner">
            <div className="mall-section-banner-copy">
              <div className="mall-section-banner-title">订单与玩家充值明细查询</div>
            </div>
            <div className="mall-section-banner-tags">
              {orderWorkspaceStats.map((item) => (
                <div key={item.label} className="mall-inline-stat">
                  <span>{item.label}</span>
                  <strong className="mall-dynamic-color" style={{ '--mall-dynamic-color': item.accent }}>{item.value}</strong>
                </div>
              ))}
            </div>
          </div>

          <div className="mall-command-panel">
            <div className="mall-command-panel-main">
              <OrderLookupInput value={orderSearchText} onChange={setOrderSearchText}
                onSearch={keyword => { setOrderSearchText(keyword); setOrderFilters({ keyword }); setOrderPagination(prev => ({ ...prev, current: 1 })); }} />
              <Select placeholder="全部区服" value={orderFilters.server_id} className="field-width-100" allowClear onChange={(value) => { setOrderFilters(prev => ({ ...prev, server_id: value })); setOrderPagination(prev => ({ ...prev, current: 1 })); }}>
                {mallZoneOptions.map((zone) => <Option key={zone.value} value={zone.value}>{zone.label}</Option>)}
              </Select>
              <Select placeholder="支付结果" value={orderFilters.status_group} className="field-width-130" allowClear onChange={(value) => { setOrderFilters(prev => ({ ...prev, status: undefined, status_group: value })); setOrderPagination(prev => ({ ...prev, current: 1 })); }}>
                <Option value="success">支付成功</Option>
                <Option value="canceled">支付取消</Option>
                <Option value="failed">支付失败</Option>
              </Select>
              <Select placeholder="详细状态" value={orderFilters.status} className="field-width-130" allowClear onChange={(value) => { setOrderFilters(prev => ({ ...prev, status_group: undefined, status: value })); setOrderPagination(prev => ({ ...prev, current: 1 })); }}>{orderStatusOptions.map((option) => <Option key={option.value} value={option.value}>{option.label}</Option>)}</Select>
              <Select placeholder="支付方式" value={orderFilters.payment_method} className="field-width-130" allowClear onChange={(value) => { setOrderFilters(prev => ({ ...prev, payment_method: value })); setOrderPagination(prev => ({ ...prev, current: 1 })); }}>
                {paymentMethodOptions.map(option => <Option key={option.value} value={option.value}>{option.label}</Option>)}
              </Select>
              <RangePicker value={orderFilters.start_date && orderFilters.end_date ? [dayjs(orderFilters.start_date), dayjs(orderFilters.end_date)] : null} onChange={(dates) => { if (dates?.[0] && dates?.[1]) { setOrderFilters(prev => ({ ...prev, start_date: dates[0].format('YYYY-MM-DD'), end_date: dates[1].format('YYYY-MM-DD') })); } else { setOrderFilters(prev => ({ ...prev, start_date: undefined, end_date: undefined })); } setOrderPagination(prev => ({ ...prev, current: 1 })); }} />
            </div>
            <div className="mall-command-panel-actions">
              <Button type="primary" icon={<PlusOutlined />} onClick={() => setManualOrderVisible(true)}>人工补订单</Button>
              <Button onClick={() => { setOrderFilters({}); setOrderSearchText(''); setOrderPagination(prev => ({ ...prev, current: 1 })); }}>清空查询和筛选</Button>
              <Button icon={<ReloadOutlined />} onClick={loadOrders} className="rounded-button">刷新</Button>
            </div>
          </div>

          <div className="block-gap-lg">
            <Text type="secondary">一个输入框查询全部区服的商城、新旧客服代充与平台币补发订单。提交关键词会清空旧筛选；可在查询后缩小范围。不补单、不发币，现金及返佣口径不变。</Text>
            {(orderFilters.keyword || orderQueryError) && <Alert showIcon style={{ marginTop: 10 }} type={orderQueryError ? 'error' : 'info'}
              message={orderQueryError || `查询关键词：${orderFilters.keyword} · ${loading ? '查询中…' : `共 ${orderPagination.total} 笔订单`}`} />}
          </div>

          <Card className="section-card" size="small">
            <Space wrap size="large">
              <DatePicker value={dayjs(orderSummaryDate)} onChange={(date) => setOrderSummaryDate(date ? date.format('YYYY-MM-DD') : dayjs().format('YYYY-MM-DD'))} />
              {mallZoneOptions.map((zone) => (
                <Statistic key={zone.value} title={`${zone.label}所选日期销售额`} value={Number(orderSummary.zones?.[String(zone.value)]?.revenue || 0)} precision={2} prefix="¥" />
              ))}
              <Tag color="blue">日期：{orderSummary.date || orderSummaryDate}</Tag>
            </Space>
          </Card>

          <Card className="section-card mall-data-card" bodyStyle={{ padding: 0 }}>
            <div className="mall-data-card-head">
              <div>
                <div className="mall-data-card-title">订单列表</div>
              </div>
              <Tag color="green">当前页 {orders.length} 条</Tag>
            </div>
            <Table columns={orderColumns} dataSource={orders} loading={loading} pagination={{ ...orderPagination, showSizeChanger: true, showTotal: (total) => `共 ${total} 条`, onChange: (page, pageSize) => setOrderPagination(prev => ({ ...prev, current: page, pageSize: pageSize || 20 })) }} rowKey="id" scroll={{ x: 1500 }} locale={{ emptyText: renderEmptyPanel('暂无充值订单', '请核对玩家账号、订单号、状态或日期范围。') }} />
          </Card>
        </>
      )}

      <Modal
        title="人工补订单"
        open={manualOrderVisible}
        onCancel={() => { setManualOrderVisible(false); manualOrderForm.resetFields(); }}
        onOk={() => manualOrderForm.submit()}
        okText="确认记录"
        cancelText="取消"
        destroyOnClose
      >
        <Form form={manualOrderForm} layout="vertical" onFinish={handleCreateManualOrder} initialValues={{ server_id: 1, payment_method: 'manual' }}>
          <Form.Item name="order_no" label="自定义订单号" rules={[{ required: true, message: '请输入订单号' }]}>
            <Input placeholder="必须唯一，例如 MANUAL202608180001" />
          </Form.Item>
          <Row gutter={16}>
            <Col span={12}><Form.Item name="amount" label="金额" rules={[{ required: true, message: '请输入金额' }]}><InputNumber min={0.01} precision={2} className="field-width-full" prefix="¥" /></Form.Item></Col>
            <Col span={12}><Form.Item name="server_id" label="归属区服" rules={[{ required: true }]}><Select>{mallZoneOptions.map((zone) => <Option key={zone.value} value={zone.value}>{zone.label}</Option>)}</Select></Form.Item></Col>
          </Row>
          <Form.Item name="agent_name" label="代理/经办人" rules={[{ required: true, message: '请输入代理名称' }]}><Input /></Form.Item>
          <Form.Item name="payment_method" label="支付方式" rules={[{ required: true }]}><Select><Option value="manual">人工</Option><Option value="cash">现金</Option><Option value="bank_transfer">银行转账</Option><Option value="crypto">数字货币</Option></Select></Form.Item>
          <Form.Item name="remark" label="备注"><Input.TextArea rows={3} maxLength={500} showCount /></Form.Item>
          <div className="page-note">人工补订单只用于财务留痕和查询，不触发游戏发货，也不计入真实支付流水。</div>
        </Form>
      </Modal>

      <Modal title={modalMode === 'view' ? '商品详情' : currentProduct ? '编辑商品' : '创建商品'} open={productModalVisible} onCancel={() => { setProductModalVisible(false); setSelectedItems([]); }}
        footer={modalMode === 'view' ? <Button onClick={() => setProductModalVisible(false)} className="rounded-button">关闭</Button> : undefined}
        onOk={modalMode !== 'view' ? () => productForm.submit() : undefined} width={960} okText="保存" cancelText="取消" destroyOnClose
      >
        {modalMode === 'view' && currentProduct ? (
          <div>
            <Row gutter={24}>
              <Col span={8}>
                <div className="mall-product-preview mall-product-preview-dynamic" style={{ '--mall-preview-bg': currentProductTypeTheme.surface.gradient }}>
                  {currentProduct.image_url ? <Image src={currentProduct.image_url} alt={currentProduct.name} className="mall-product-preview-image" /> : <span className="mall-product-preview-icon">{currentProductTypeTheme.icon.glyph}</span>}
                </div>
              </Col>
              <Col span={16}>
                <Title level={4} className="mall-product-title">{currentProduct.name}</Title>
                <Space className="mall-product-tags" wrap>
                  <Tag color={currentProductTypeTheme.badge.color}>{currentProductTypeTheme.label}</Tag>
                  <Tag color={currentProductCurrencyTheme.badge.color}>{currentProductCurrencyTheme.label}</Tag>
                  {currentProduct.is_hot === 1 && <Tag color="red"><FireFilled /> 热销</Tag>}
                  {currentProduct.is_new === 1 && <Tag color="green"><StarFilled /> 新品</Tag>}
                  <Tag color={currentProduct.status === 1 ? 'green' : 'default'}>{currentProduct.status === 1 ? '在售' : '已下架'}</Tag>
                </Space>
                <div className="mall-price-panel">
                  {currentProduct.original_price > 0 && currentProduct.original_price !== (currentProduct.currency_price || currentProduct.current_price) && <Text delete className="text-size-md text-faint">{currentProductCurrencyTheme.symbol}{currentProduct.original_price}</Text>}
                  <Text strong className="mall-price-current-lg text-accent-danger mall-price-gap">{currentProductCurrencyTheme.symbol}{currentProduct.currency_price || currentProduct.current_price}</Text>
                  {currentProduct.discount && currentProduct.discount < 1 && currentProduct.discount > 0 && <Tag color="red" className="mall-discount-tag-lg">{Math.round(currentProduct.discount * 10)}折</Tag>}
                </div>
                <Descriptions column={2} size="small" bordered>
                  <Descriptions.Item label="库存">{currentProduct.stock === -1 ? '无限' : currentProduct.stock}</Descriptions.Item>
                  <Descriptions.Item label="总限购">{currentProduct.limit_per_user > 0 ? `每人${currentProduct.limit_per_user}件` : '不限'}</Descriptions.Item>
                  <Descriptions.Item label="每日限购">{currentProduct.daily_limit_per_user > 0 ? `每日${currentProduct.daily_limit_per_user}件` : '不限'}</Descriptions.Item>
                  <Descriptions.Item label="排序">{currentProduct.sort_order}</Descriptions.Item>
                  <Descriptions.Item label="描述">{currentProduct.description || '无'}</Descriptions.Item>
                </Descriptions>
              </Col>
            </Row>
            {currentProduct.type === 'bubble_vip' && (() => {
              const itemData = parseProductConfig(currentProduct);
              return (
                <div className="mall-section-top">
                  <Divider orientation="left">VIP配置</Divider>
                  <Descriptions column={2} size="small" bordered>
                    <Descriptions.Item label="功能">泡点VIP</Descriptions.Item>
                    <Descriptions.Item label="时长">{itemData.duration_value || 30} 天</Descriptions.Item>
                  </Descriptions>
                </div>
              );
            })()}
            {currentProduct.type === 'aoz' && (() => {
              const itemData = parseProductConfig(currentProduct);
              const configured = Object.prototype.hasOwnProperty.call(itemData, 'first_charge_bonus_aoz');
              return (
                <div className="mall-section-top">
                  <Divider orientation="left">首充配置</Divider>
                  <Descriptions column={2} size="small" bordered>
                    <Descriptions.Item label="首充周期">每天 00:00 刷新</Descriptions.Item>
                    <Descriptions.Item label="首充额外赠送">
                      {configured ? `${Math.max(0, Number(itemData.first_charge_bonus_aoz) || 0)} AOZ` : '沿用旧全局百分比'}
                    </Descriptions.Item>
                  </Descriptions>
                </div>
              );
            })()}
            {selectedItems.length > 0 && (
              <div className="mall-section-top">
                <Divider orientation="left">商品内容</Divider>
                <div className="selection-panel">
                  <div className="selection-panel-head">
                    <div>
                      <div className="selection-panel-title">已配置内容</div>
                      <div className="selection-panel-subtitle">查看商品发放的道具、资源或 AOZ 组合。</div>
                    </div>
                    <Tag color="green">共 {selectedItems.length} 项</Tag>
                  </div>
                  <div className="selection-list">
                    {selectedItems.map((item) => (
                      <div className="selection-item" key={item.uniqueId || item.id}>
                        <div className="selection-item-main">
                          <div className="selection-item-title">{item.name}</div>
                          <div className="selection-item-meta">类型 {item.type || '道具'}{item.level > 0 ? ` / Lv.${item.level}` : ''}</div>
                        </div>
                        <div className="selection-item-controls">
                          <Tag>x{item.count}</Tag>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}
          </div>
        ) : (
          <Form form={productForm} onFinish={handleProductSubmit} layout="vertical" size="middle">
            <div className="page-note page-note-flat">
              <div className="page-note-text">
                <span className="page-note-strong">{currentProduct ? '正在编辑现有商品。' : '正在创建新商品。'}</span>
                先确定商品类型和货币，再补内容、价格、库存和上下架状态，避免后面重复调整。
              </div>
            </div>
            <Row gutter={16}>
              <Col span={12}><Form.Item name="name" label="商品名称" rules={[{ required: true, message: '请输入商品名称' }]}><Input placeholder="请输入商品名称" /></Form.Item></Col>
              <Col span={6}><Form.Item name="type" label="商品类型" rules={[{ required: true, message: '请选择商品类型' }]}><Select placeholder="请选择商品类型">{productTypeOptions.map((option) => <Option key={option.value} value={option.value}>{option.label}</Option>)}</Select></Form.Item></Col>
              <Col span={6}><Form.Item name="currency_type" label="货币类型" rules={[{ required: true, message: '请选择货币类型' }]}><Select placeholder="请选择货币类型">{currencyOptions.map((option) => <Option key={option.value} value={option.value}>{option.label}</Option>)}</Select></Form.Item></Col>
            </Row>
            <Row gutter={16}>
              <Col span={16}><Form.Item name="image_url" label="商品图片 URL"><Input placeholder="选填，不填则自动生成默认图片" /></Form.Item></Col>
              <Col span={8}><Form.Item name="discount" label="折扣 (0.1-1)"><InputNumber className="field-width-full" placeholder="请输入折扣，例如 1" min={0.1} max={1} step={0.1} precision={1} /></Form.Item></Col>
            </Row>
            <Form.Item name="description" label="商品描述"><TextArea rows={2} placeholder="请输入商品描述" /></Form.Item>
            <div className="first-charge-config-card block-gap-md">
              <div className="first-charge-config-copy">
                <span className="first-charge-config-title">首充奖励配置（仅 AOZ 商品生效）。</span>
                {productType === 'aoz'
                  ? '例如现价 6 元、商品内容 1000 AOZ、首充额外赠送 700，则该档玩家当天第一次成功支付后实得 1700 AOZ。每天 00:00 刷新；每个 AOZ 商品档位每天各有一次首充奖励。'
                  : '请先在上方“商品类型”选择 AOZ，随后在这里填写首充额外赠送数量。'}
              </div>
              <Form.Item
                name="first_charge_bonus_aoz"
                label="首充额外赠送 AOZ"
                extra={productType === 'aoz'
                  ? '填 0 可明确关闭该商品首充；留空则沿用旧的全局百分比配置，直到为该商品设置固定值。'
                  : '当前不是 AOZ 商品，因此此配置不会保存或参与发奖。'}
              >
                <InputNumber
                  min={0}
                  max={100000000}
                  precision={0}
                  className="field-width-full"
                  placeholder="例如 700"
                  addonAfter="AOZ"
                  disabled={productType !== 'aoz'}
                />
              </Form.Item>
            </div>
            {productType === 'bubble_vip' ? (
              <Row gutter={16}>
                <Col span={12}>
                  <Form.Item name="vip_duration_value" label="VIP天数" rules={[{ required: true, message: '请输入VIP天数' }]}>
                    <InputNumber min={1} className="field-width-full" placeholder="请输入 VIP 天数，例如 30" />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item name="vip_duration_unit" label="时长单位" initialValue="day">
                    <Select>
                      <Option value="day">天</Option>
                    </Select>
                  </Form.Item>
                </Col>
              </Row>
            ) : (
              <>
                <div className="section-note block-gap-md">
                  <div className="section-note-text">
                    <span className="section-note-strong">商品内容决定实际发放给玩家的资源或道具。</span>
                    可以从资源、道具和 AOZ 三类里组合选择。
                  </div>
                  {selectedItems.length > 0 ? <Tag color="green">已选 {selectedItems.length} 项内容</Tag> : <Text type="secondary">暂未选择内容</Text>}
                </div>
                <Form.Item label="商品内容">
                  <Space><Button type="primary" icon={<GiftOutlined />} onClick={() => setItemModalVisible(true)} className="rounded-button">选择内容</Button>{selectedItems.length > 0 && <Tag color="green" className="tag-tight">已选 {selectedItems.length} 项内容</Tag>}</Space>
                </Form.Item>
              </>
            )}
            {productType !== 'bubble_vip' && selectedItems.length > 0 && (
              <div className="selection-panel block-gap-md">
                <div className="selection-panel-head">
                  <div>
                    <div className="selection-panel-title">已选内容 ({selectedItems.length})</div>
                    <div className="selection-panel-subtitle">创建商品前可继续调整发放数量或删除单项。</div>
                  </div>
                </div>
                <div className="selection-list">
                  {selectedItems.map((item) => (
                    <div className="selection-item" key={item.uniqueId || item.id}>
                      <div className="selection-item-main">
                        <div className="selection-item-title">{item.name}{item.level > 0 ? ` Lv.${item.level}` : ''}</div>
                        <div className="selection-item-meta">ID {item.id} / 类型 {item.type || '道具'}</div>
                      </div>
                      <div className="selection-item-controls">
                        <InputNumber size="small" min={1} value={item.count} onChange={(value) => handleUpdateItemCount(item.uniqueId || item.id, value)} className="field-width-72" />
                        <Button size="small" danger type="text" onClick={() => handleRemoveItem(item.uniqueId || item.id)} icon={<DeleteOutlined />} />
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
            <Divider orientation="left">价格设置</Divider>
            <Row gutter={16}>
              <Col span={8}><Form.Item name="original_price" label="原价" rules={[{ type: 'number', min: 0 }]}><InputNumber className="field-width-full" placeholder="请输入原价" min={0} precision={2} addonBefore="¥" /></Form.Item></Col>
              <Col span={8}><Form.Item name="current_price" label="现价" rules={[{ type: 'number', min: 0, required: true, message: '请输入现价' }]}><InputNumber className="field-width-full" placeholder="请输入现价" min={0} precision={2} addonBefore="¥" /></Form.Item></Col>
              <Col span={8}><Form.Item name="currency_price" label="货币价格" rules={[{ type: 'number', min: 0 }]}><InputNumber className="field-width-full" placeholder="请输入货币价格" min={0} precision={2} /></Form.Item></Col>
            </Row>
            <Divider orientation="left">库存与限购</Divider>
            <Row gutter={16}>
              <Col span={8}><Form.Item name="stock" label="库存 (-1 为无限)" rules={[{ type: 'number' }]}><InputNumber className="field-width-full" placeholder="请输入库存，-1 表示无限" min={-1} /></Form.Item></Col>
              <Col span={8}>
                <Form.Item
                  name="limit_per_user"
                  label="总限购（0 为不限）"
                  extra="仅控制是否允许继续购买，与首充额外赠送次数无关。"
                  rules={[{ type: 'number', min: 0 }]}
                >
                  <InputNumber className="field-width-full" placeholder="请输入总限购次数，0 为不限" min={0} />
                </Form.Item>
              </Col>
              <Col span={8}><Form.Item name="daily_limit_per_user" label="每日购买次数限制" rules={[{ type: 'number', min: 0 }]}><InputNumber className="field-width-full" placeholder="请输入每日限购次数，0 为不限" min={0} /></Form.Item></Col>
            </Row>
            <Row gutter={16}>
              <Col span={8}><Form.Item name="sort_order" label="排序" rules={[{ type: 'number' }]}><InputNumber className="field-width-full" placeholder="请输入排序值" min={0} /></Form.Item></Col>
            </Row>
            <Divider orientation="left">状态设置</Divider>
            <Row gutter={16}>
              <Col span={8}><Form.Item name="status" label="状态" valuePropName="checked" getValueFromEvent={(checked) => checked ? 1 : 0}><Switch checkedChildren="上架" unCheckedChildren="下架" /></Form.Item></Col>
              <Col span={8}><Form.Item name="is_hot" label="热销" valuePropName="checked" getValueFromEvent={(checked) => checked ? 1 : 0}><Switch checkedChildren="是" unCheckedChildren="否" /></Form.Item></Col>
              <Col span={8}><Form.Item name="is_new" label="新品" valuePropName="checked" getValueFromEvent={(checked) => checked ? 1 : 0}><Switch checkedChildren="是" unCheckedChildren="否" /></Form.Item></Col>
            </Row>
            <Row gutter={16}>
              <Col span={12}><Form.Item name="start_time" label="上架时间"><DatePicker showTime className="field-width-full" /></Form.Item></Col>
              <Col span={12}><Form.Item name="end_time" label="下架时间"><DatePicker showTime className="field-width-full" /></Form.Item></Col>
            </Row>
          </Form>
        )}
      </Modal>

      <Modal title={<span><ProfileOutlined className="icon-gap-sm" />充值订单详情</span>} open={orderDetailVisible} onCancel={() => setOrderDetailVisible(false)} footer={null} width={960}>
        {currentOrder && (
          <>
            <div className="section-note block-gap-md">
              <div className="section-note-text">
                <span className="section-note-strong">订单详情用于核对玩家、商品和发货状态。</span>
                普通支付订单可在核实后推进；后台直接发币的待核实订单禁止再次发货。
              </div>
              <Tag color={currentOrderStatusTheme.badge.color}>{currentOrderStatusTheme.label}</Tag>
            </div>
            <Row gutter={24}>
              <Col span={16}>
                <Descriptions column={2} bordered size="small">
                  <Descriptions.Item label="订单号" span={2}>{currentOrder.order_no}</Descriptions.Item>
                  <Descriptions.Item label="支付单号" span={2}>{currentOrder.payment_order_no || '-'}</Descriptions.Item>
                  <Descriptions.Item label="渠道流水号" span={2}>{currentOrder.trade_no || '-'}</Descriptions.Item>
                  <Descriptions.Item label="到账账号"><Text strong>{currentOrder.target_account_name || '-'}</Text></Descriptions.Item>
                  <Descriptions.Item label="付款账号">{currentOrder.payer_account_name || currentOrder.target_account_name || '-'}</Descriptions.Item>
                  <Descriptions.Item label="用户 ID">{currentOrder.user_id}</Descriptions.Item>
                  <Descriptions.Item label="付款用户 ID">{currentOrder.payer_user_id || currentOrder.user_id || '-'}</Descriptions.Item>
                  <Descriptions.Item label="角色名称">{currentOrder.character_name || '-'}</Descriptions.Item>
                  <Descriptions.Item label="角色 ID">{currentOrder.character_id || '-'}</Descriptions.Item>
                  <Descriptions.Item label="区服">{currentOrder.server_id ? `${currentOrder.server_id}服` : '-'}</Descriptions.Item>
                  <Descriptions.Item label="下单IP">{currentOrder.client_ip || '-'}</Descriptions.Item>
                  {currentOrder.product_type === 'admin_direct_grant' && (() => {
                    try {
                      const operation = typeof currentOrder.item_data === 'string' ? JSON.parse(currentOrder.item_data) : currentOrder.item_data;
                      return <>
                        <Descriptions.Item label="操作员">{operation.operatorName || operation.operatorId}</Descriptions.Item>
                        <Descriptions.Item label="计账方式">{operation.accountingMode === 'cumulative' ? '计有效累充，不计现金' : '运营赠币，不计充值'}</Descriptions.Item>
                        <Descriptions.Item label="请求编号" span={2}>{operation.requestId}</Descriptions.Item>
                        <Descriptions.Item label="操作依据" span={2}>{operation.remark}</Descriptions.Item>
                      </>;
                    } catch (error) { return <Descriptions.Item label="操作记录" span={2}>记录格式异常，请保留订单号核查</Descriptions.Item>; }
                  })()}
                  <Descriptions.Item label="支付渠道">{currentOrder.payment_provider || '-'}</Descriptions.Item>
                  <Descriptions.Item label="商品名称">{currentOrder.product_name}</Descriptions.Item>
                  <Descriptions.Item label="商品类型">{currentOrder.product_type || currentProduct?.product_type || '-'}</Descriptions.Item>
                  <Descriptions.Item label="购买数量">{currentOrder.quantity}</Descriptions.Item>
                  <Descriptions.Item label="订单金额"><Text type="danger" strong>¥{currentOrder.total_amount}</Text></Descriptions.Item>
                  <Descriptions.Item label="支付方式">{currentOrder.payment_method ? <Tag color="purple" className="tag-tight">{paymentMethodLabel(currentOrder.payment_method)}</Tag> : '-'}</Descriptions.Item>
                  <Descriptions.Item label="订单状态"><Tag color={currentOrderStatusTheme.badge.color} className="tag-tight">{currentOrderStatusTheme.label}</Tag></Descriptions.Item>
                  <Descriptions.Item label="发货/到账状态">{renderFulfillmentState(currentOrder)}</Descriptions.Item>
                  <Descriptions.Item label="发货尝试次数">{Number(currentOrder.fulfillment_attempts || 0)}</Descriptions.Item>
                  <Descriptions.Item label="首充奖励">{renderFirstChargeState(currentOrder.first_charge_claim_status)}</Descriptions.Item>
                  <Descriptions.Item label="首充奖励AOZ">{Number(currentOrder.first_charge_bonus_aoz || 0).toLocaleString()}</Descriptions.Item>
                  <Descriptions.Item label="下单时间">{currentOrder.create_time ? new Date(currentOrder.create_time).toLocaleString('zh-CN') : '-'}</Descriptions.Item>
                  <Descriptions.Item label="支付时间">{currentOrder.payment_time ? new Date(currentOrder.payment_time).toLocaleString('zh-CN') : '-'}</Descriptions.Item>
                  <Descriptions.Item label="完成时间">{currentOrder.finish_time ? new Date(currentOrder.finish_time).toLocaleString('zh-CN') : '-'}</Descriptions.Item>
                  <Descriptions.Item label="最近到账时间">{currentOrder.last_delivered_at ? new Date(currentOrder.last_delivered_at).toLocaleString('zh-CN') : '-'}</Descriptions.Item>
                  {(currentOrder.fulfillment_last_error || currentOrder.first_charge_error || currentOrder.cancel_reason) && (
                    <Descriptions.Item label="异常/取消原因" span={2}>
                      <Text type="danger">{currentOrder.fulfillment_last_error || currentOrder.first_charge_error || currentOrder.cancel_reason}</Text>
                    </Descriptions.Item>
                  )}
                  <Descriptions.Item label="商品内容" span={2}>
                    {(() => {
                      try {
                        const itemData = typeof currentOrder.item_data === 'string' ? JSON.parse(currentOrder.item_data) : currentOrder.item_data;
                        if (itemData?.mode === 'bubble_vip') {
                          return <Tag color="gold">泡点VIP {itemData.duration_value || 30} 天</Tag>;
                        }
                        if (itemData?.mode === 'platform_coin_manual_grant') {
                          return (
                            <Space direction="vertical" size={4}>
                              <Tag color="purple">人工补发 {Number(itemData.platform_coins || currentOrder.total_amount || 0).toFixed(2)} 平台币</Tag>
                              <Text>备注：{itemData.remark || '-'}</Text>
                              <Text type="secondary">操作人：{itemData.operator_name || itemData.operator_id || '-'}</Text>
                            </Space>
                          );
                        }
                        if (itemData?.items && Array.isArray(itemData.items)) {
                          return (
                            <div className="selection-panel">
                              <div className="selection-list">
                                {itemData.items.map((item, index) => (
                                  <div className="selection-item" key={index}>
                                    <div className="selection-item-main">
                                      <div className="selection-item-title">{item.name || `道具 ${item.id || index}`}</div>
                                      <div className="selection-item-meta">类型 {item.type || '道具'} / ID {item.id || '-'}</div>
                                    </div>
                                    <div className="selection-item-controls">
                                      <Tag color="blue">x{item.count || item.quantity || 1}</Tag>
                                    </div>
                                  </div>
                                ))}
                              </div>
                            </div>
                          );
                        }
                        return <Text type="secondary">暂无道具信息</Text>;
                      } catch (error) {
                        return <Text type="danger">道具数据解析失败</Text>;
                      }
                    })()}
                  </Descriptions.Item>
                </Descriptions>
              </Col>
              <Col span={8}><Card size="small" title="订单进度" className="mall-order-progress-card">{renderOrderTimeline(currentOrder)}</Card></Col>
            </Row>
            {currentOrder.status < 3 && currentOrder.product_type !== 'admin_direct_grant' && (
              <>
                <Divider />
                <div className="highlight-panel">
                  <div className="section-toolbar">
                    <div className="section-toolbar-main">
                      <div className="table-cell">
                        <div className="table-cell-primary">人工推进订单状态</div>
                        <div className="table-cell-secondary">只在确认支付、发货或补偿流程后再手动修改。</div>
                      </div>
                    </div>
                    <div className="action-group">
                      {currentOrder.status === 0 && <Button type="primary" onClick={() => handleUpdateOrderStatus(currentOrder.id, 1)} className="rounded-button">标记为已支付</Button>}
                      {currentOrder.status <= 1 && <Button onClick={() => handleUpdateOrderStatus(currentOrder.id, 2)} className="rounded-button">标记为已发货</Button>}
                      {currentOrder.status <= 2 && <Button className="rounded-button" onClick={() => handleUpdateOrderStatus(currentOrder.id, 3)}>标记为已完成</Button>}
                      {currentOrder.status < 4 && <Button danger onClick={() => handleUpdateOrderStatus(currentOrder.id, 4)} className="rounded-button">标记为已取消</Button>}
                    </div>
                  </div>
                </div>
              </>
            )}
          </>
        )}
      </Modal>

      <Modal
        title={null}
        open={itemModalVisible}
        onCancel={() => setItemModalVisible(false)}
        onOk={handleConfirmItems}
        width={1120}
        okText="确认选择"
        cancelText="取消"
        className="mail-item-modal"
      >
        <div className="mail-item-picker">
          <div className="mail-modal-hero mail-modal-hero-compact">
            <div>
              <div className="mail-modal-title">选择上架内容</div>
            </div>
            <div className="mail-modal-hero-tags">
              <Tag color="blue">可选 {itemList.filter((item) => {
                if (!itemSearchText) return true;
                const search = itemSearchText.toLowerCase();
                return String(item.id).toLowerCase().includes(search) || (item.name || '').toLowerCase().includes(search);
              }).length} 个</Tag>
              <Tag color="green">已选 {selectedItems.length} 项</Tag>
            </div>
          </div>

          <div className="mail-item-picker-toolbar">
            <Input.Search
              placeholder="搜索资源、道具 ID 或名称"
              prefix={<SearchOutlined />}
              value={itemSearchText}
              onChange={(e) => setItemSearchText(e.target.value)}
              className="mail-item-search"
              allowClear
            />
          </div>

          <div className="mail-item-picker-body">
            <div className="mail-item-library">
              <div className="mail-item-grid">
                {itemList.filter((item) => {
                  if (!itemSearchText) return true;
                  const search = itemSearchText.toLowerCase();
                  return String(item.id).toLowerCase().includes(search) || (item.name || '').toLowerCase().includes(search);
                }).map((record) => {
                  const singleLevel = record.type === 'diamond' ? 0 : (record.defaultLevel || 1);
                  const isAdded = record.hasMultipleLevels
                    ? record.levels.some((lvl) => selectedItems.some((si) => si.uniqueId === `${record.id}_${lvl.level}`))
                    : selectedItems.some((si) => si.uniqueId === `${record.id}_${singleLevel}`);
                  const typeColor = record.type === 'diamond' ? 'cyan' : record.type === 'aoz' ? 'purple' : 'blue';
                  const typeLabel = record.type === 'diamond' ? '资源' : record.type === 'aoz' ? 'AOZ' : '道具';

                  return (
                    <div key={`${record.type}-${record.id}`} className={`mail-item-card${isAdded ? ' mail-item-card-added' : ''}`}>
                      <div className="mail-item-card-head">
                        <div>
                          <div className="mail-item-card-title">{record.name || '未命名内容'}</div>
                          <div className="mail-item-card-subtitle">ID {record.id}</div>
                        </div>
                        <Tag color={typeColor}>{typeLabel}</Tag>
                      </div>
                      <div className="mail-item-card-meta">
                        <span>{record.levels?.length > 0 ? `${record.levels.length} 个等级` : '固定内容'}</span>
                        <span>{record.type || 'item'}</span>
                      </div>
                      <div className="mail-item-card-desc">{record.description || '可直接加入商品的发放内容。'}</div>
                      <Button
                        type={isAdded ? 'default' : 'primary'}
                        onClick={() => handleSelectItem(record)}
                        disabled={isAdded}
                        block
                      >
                        {isAdded ? '已添加' : '加入上架内容'}
                      </Button>
                    </div>
                  );
                })}
              </div>
            </div>

            <div className="mail-item-sidebar">
              <div className="mail-form-section mail-form-section-tight">
                <div className="mail-form-section-head">
                  <div className="mail-form-section-title">已选上架内容</div>
                  <div className="mail-form-section-subtitle">确认后会直接写入商品配置，可继续调整数量或移除。</div>
                </div>
                {selectedItems.length > 0 ? (
                  <div className="mail-selected-list">
                    {selectedItems.map((item) => (
                      <div className="mail-selected-item" key={item.uniqueId || item.id}>
                        <div className="mail-selected-item-main">
                          <div className="mail-selected-item-title">{item.name}{item.level > 0 ? ` Lv.${item.level}` : ''}</div>
                          <div className="mail-selected-item-meta">ID {item.id} / 类型 {item.type || '道具'}</div>
                        </div>
                        <div className="mail-selected-item-controls">
                          <InputNumber
                            size="small"
                            min={1}
                            value={item.count}
                            onChange={(value) => handleUpdateItemCount(item.uniqueId || item.id, value)}
                            addonBefore="数量"
                            className="field-width-120"
                          />
                          <Button size="small" danger type="text" onClick={() => handleRemoveItem(item.uniqueId || item.id)}>
                            移除
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="mail-empty-panel">
                    <div className="mail-empty-panel-title">暂未添加商城内容</div>
                    <div className="mail-empty-panel-text">从左侧物品库加入资源、道具或 AOZ 后，这里会同步显示。</div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </Modal>

      <Modal title={`选择等级 - ${currentItemForLevel?.name || ''}`} open={levelModalVisible} onCancel={() => { setLevelModalVisible(false); setCurrentItemForLevel(null); setSelectedLevel(null); }} onOk={handleConfirmLevel} width={440} okText="确认选择" cancelText="取消">
        <div className="scroll-panel-300">
          <Table columns={[
            { title: '等级', dataIndex: 'level', key: 'level', width: 70, render: (level) => <Tag color="blue" className="tag-tight">Lv.{level}</Tag> },
            { title: '品质', dataIndex: 'quality', key: 'quality', width: 70, render: (quality) => <Tag className="tag-tight">{quality || '-'}</Tag> },
            { title: '描述', dataIndex: 'description', key: 'description', width: 150, ellipsis: true, render: (text) => <Text type="secondary" ellipsis={{ tooltip: text || '-' }}>{text || '-'}</Text> },
            { title: '操作', key: 'action', width: 88, render: (_, record) => <Button size="small" type={selectedLevel === record.level ? 'primary' : 'default'} onClick={() => setSelectedLevel(record.level)} className="rounded-button">{selectedLevel === record.level ? '已选' : '选择'}</Button> }
          ]} dataSource={currentItemForLevel?.levels || []} rowKey="level" pagination={false} size="small" tableLayout="fixed" scroll={{ x: 378 }} />
        </div>
      </Modal>
    </div>
  );
}

export default MallManagement;
